// src/client/router/index.js

import { createRouter, createMemoryHistory } from 'vue-router';
import { ref } from 'vue';

import AppShell from '@/layouts/AppShell.vue';
import PublicShell from '@/layouts/PublicShell.vue';

import LoginView from '@/views/LoginView.vue';
import { useAuth } from '@/composables/useAuth.js';

import PimGridView from '@/views/pim/PimGridView.vue';

import CpConfigView from '@/views/cp/CpConfigView.vue';
import CpImportView from '@/views/cp/CpImportView.vue';
import CpExportView from '@/views/cp/CpExportView.vue';
import CpSyncView from '@/views/cp/CpSyncView.vue';

import SapConfigView from '@/views/sap/SapConfigView.vue';
import SapSyncView from '@/views/sap/SapSyncView.vue';
import SapExportView from '@/views/sap/SapExportView.vue';

import BaseConfigView from '@/views/base/BaseConfigView.vue';
import BaseImportView from '@/views/base/BaseImportView.vue';
import BaseExportView from '@/views/base/BaseExportView.vue';

import BmdConfigView from '@/views/bmd/BmdConfigView.vue';
import BmdImportView from '@/views/bmd/BmdImportView.vue';
import BmdExportView from '@/views/bmd/BmdExportView.vue';

/* ============================================================
   Routes (INTERNAL / MEMORY-BASED)
============================================================ */
const routes = [
  {
    path: '/login',
    component: PublicShell,
    meta: { guestOnly: true },
    children: [
      { path: '', name: 'login', component: LoginView },
    ],
  },

  {
    path: '/',
    component: AppShell,
    meta: { requiresAuth: true },

    // INTERNAL child routing (hidden from browser)
    children: [
      {
        path: 'dashboard', name: 'dashboard', component: PimGridView,
      },
      // ComfortPages views
      { path: 'cp/config',  name: 'cp-config',  component: CpConfigView },
      { path: 'cp/sync',  name: 'cp-sync',  component: CpSyncView },
      { path: 'cp/import',  name: 'cp-import',  component: CpImportView },
      { path: 'cp/export',  name: 'cp-export',  component: CpExportView },
      // SAP views
      { path: 'sap/config',  name: 'sap-config',  component: SapConfigView },
      { path: 'sap/sync',  name: 'sap-sync',  component: SapSyncView },
      { path: 'sap/export',  name: 'sap-export',  component: SapExportView },      
      // Base views
      { path: 'base/config',  name: 'base-config',  component: BaseConfigView },
      { path: 'base/import',  name: 'base-import',  component: BaseImportView },
      { path: 'base/export',  name: 'base-export',  component: BaseExportView },
      // BMD views
      { path: 'bmd/config',  name: 'bmd-config',  component: BmdConfigView },
      { path: 'bmd/import',  name: 'bmd-import',  component: BmdImportView },
      { path: 'bmd/export',  name: 'bmd-export',  component: BmdExportView },

      { path: '', redirect: '/dashboard' },
    ],
  },
];

const router = createRouter({
  history: createMemoryHistory(),   // 🔴 KEY CHANGE (Option C)
  routes,
});

/* ============================================================
   Global Spinner (UNCHANGED)
============================================================ */
const spinnerVisible = ref(false);

function showSpinner() {
  if (spinnerVisible.value) return;
  spinnerVisible.value = true;

  const el = document.createElement('div');
  el.id = 'cp-spinner';
  el.innerHTML = `
    <div style="
      position:fixed; inset:0; display:flex; align-items:center;
      justify-content:center; background:rgba(255,255,255,.8);
      z-index:9999;
    ">
      <img src="https://comfortpages.com/api/svg/infinity/2bafbc"
           width="80" height="80" alt="Loading..." />
    </div>
  `;
  document.body.appendChild(el);
}

function hideSpinner() {
  const el = document.getElementById('cp-spinner');
  if (el) el.remove();
  spinnerVisible.value = false;
}

/* ============================================================
   Safe Auth Check (UNCHANGED)
============================================================ */
let authCheckPromise = null;

async function ensureAuthenticated() {
  const auth = useAuth();
  const hasToken = !!auth.accessToken.value;

  if (!hasToken) return false;
  if (auth.user.value) return true;

  if (!authCheckPromise) {
    authCheckPromise = (async () => {
      if (await auth.fetchUser()) return true;
      if (await auth.silentRefresh()) {
        if (await auth.fetchUser()) return true;
      }
      return false;
    })();
  }

  const ok = await authCheckPromise;
  authCheckPromise = null;
  return ok;
}

/* ============================================================
   Navigation Guard (UNCHANGED)
============================================================ */
router.beforeEach(async (to, from, next) => {
  if (to.meta.guestOnly) return next();

  if (to.meta.requiresAuth) {
    showSpinner();
    const ok = await ensureAuthenticated();
    hideSpinner();

    if (ok) return next();
    return next('/login');
  }

  next();
});

export default router;
